<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  invoices: {
    type: Array,
    required: true,
  },
  unlinkedTaxInvoices: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['openSendModal']);
const { t } = useI18n();
const taxInvoiceStatus = status => {
  const labels = {
    SCHEDULED: t('BILLING.INVOICES.TAX_SCHEDULED'),
    AUTHORIZED: t('BILLING.INVOICES.TAX_AUTHORIZED'),
    PROCESSING_CANCELLATION: t('BILLING.INVOICES.TAX_PROCESSING_CANCELLATION'),
    CANCELED: t('BILLING.INVOICES.TAX_CANCELED'),
    CANCELLATION_DENIED: t('BILLING.INVOICES.TAX_CANCELLATION_DENIED'),
    ERROR: t('BILLING.INVOICES.TAX_ERROR'),
  };
  return labels[status] || status || t('BILLING.INVOICES.TAX_PROCESSING');
};
</script>

<template>
  <div
    class="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
  >
    <div class="p-5 border-b border-slate-200 dark:border-slate-800">
      <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
        {{ $t('BILLING.INVOICES.TITLE') }}
      </h2>
      <p class="text-sm text-slate-500 mt-1">
        {{ $t('BILLING.INVOICES.DESCRIPTION') }}
      </p>
    </div>
    <div class="p-5">
      <div v-if="invoices.length === 0" class="text-center py-8">
        <span
          class="i-lucide-receipt text-4xl text-slate-300 dark:text-slate-600 mb-3 block"
        />
        <p class="text-slate-500">{{ $t('BILLING.INVOICES.EMPTY') }}</p>
      </div>
      <div v-else class="overflow-x-auto">
        <table
          class="min-w-[820px] w-full text-left text-sm text-slate-600 dark:text-slate-300"
        >
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-800/50">
              <th class="py-3 px-4 font-medium rounded-l-lg">
                {{ $t('BILLING.INVOICES.DUE_DATE') }}
              </th>
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.VALUE') }}
              </th>
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.STATUS') }}
              </th>
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.TAX_TITLE') }}
              </th>
              <th class="py-3 px-4 font-medium text-right rounded-r-lg">
                {{ $t('BILLING.INVOICES.ACTION') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invoice in invoices"
              :key="invoice.id"
              class="border-b border-slate-100 dark:border-slate-800 last:border-0"
            >
              <td class="py-4 px-4">{{ invoice.dueDate }}</td>
              <td class="py-4 px-4 font-medium">
                {{ $t('BILLING.INVOICES.CURRENCY') }} {{ invoice.value }}
              </td>
              <td class="py-4 px-4">
                <span
                  :class="{
                    'bg-emerald-100 text-emerald-700':
                      invoice.status === 'RECEIVED' ||
                      invoice.status === 'CONFIRMED',
                    'bg-amber-100 text-amber-700': invoice.status === 'PENDING',
                    'bg-rose-100 text-rose-700':
                      invoice.status === 'OVERDUE' ||
                      invoice.status === 'REFUNDED',
                  }"
                  class="px-2.5 py-1 rounded-full text-xs font-semibold"
                >
                  {{
                    invoice.status === 'RECEIVED' ||
                    invoice.status === 'CONFIRMED'
                      ? $t('BILLING.INVOICES.PAID')
                      : invoice.status === 'PENDING'
                        ? $t('BILLING.INVOICES.PENDING')
                        : $t('BILLING.INVOICES.OVERDUE')
                  }}
                </span>
              </td>
              <td class="py-4 px-4 align-top">
                <div
                  v-if="invoice.tax_invoices?.length"
                  class="space-y-2 min-w-[220px]"
                >
                  <div
                    v-for="taxInvoice in invoice.tax_invoices"
                    :key="taxInvoice.id"
                    class="rounded-lg border border-slate-200 dark:border-slate-700 p-2"
                  >
                    <div class="flex items-center gap-2">
                      <span class="i-lucide-file-check text-emerald-500" />
                      <span
                        class="font-medium text-slate-700 dark:text-slate-200"
                      >
                        {{ $t('BILLING.INVOICES.TAX_LINKED_LABEL') }}
                        {{ taxInvoice.number || taxInvoice.rpsNumber || '—' }}
                      </span>
                    </div>
                    <p class="text-xs text-slate-400 mt-1">
                      {{ $t('BILLING.INVOICES.TAX_LINKED_TO_CHARGE') }}
                    </p>
                    <div class="flex items-center gap-2 mt-1 text-xs">
                      <span
                        :class="{
                          'text-emerald-600':
                            taxInvoice.status === 'AUTHORIZED',
                          'text-amber-600':
                            taxInvoice.status === 'SCHEDULED' ||
                            taxInvoice.status === 'PROCESSING',
                          'text-rose-600':
                            taxInvoice.status === 'ERROR' ||
                            taxInvoice.status === 'CANCELED' ||
                            taxInvoice.status === 'CANCELLATION_DENIED',
                        }"
                        class="font-semibold"
                      >
                        {{ taxInvoiceStatus(taxInvoice.status) }}
                      </span>
                      <span class="text-slate-400">
                        {{
                          taxInvoice.effectiveDate ||
                          taxInvoice.scheduledDate ||
                          '—'
                        }}
                      </span>
                    </div>
                    <div class="flex items-center gap-2 mt-1 text-xs">
                      <a
                        v-if="taxInvoice.pdfUrl"
                        :href="taxInvoice.pdfUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {{ $t('BILLING.INVOICES.TAX_PDF') }}
                      </a>
                      <a
                        v-if="taxInvoice.xmlUrl"
                        :href="taxInvoice.xmlUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-slate-600 hover:text-slate-900 font-medium"
                      >
                        {{ $t('BILLING.INVOICES.TAX_XML') }}
                      </a>
                      <span
                        v-if="!taxInvoice.pdfUrl && !taxInvoice.xmlUrl"
                        class="text-slate-400"
                      >
                        {{ $t('BILLING.INVOICES.TAX_UNAVAILABLE') }}
                      </span>
                    </div>
                  </div>
                </div>
                <span v-else class="text-xs text-slate-400">
                  {{ $t('BILLING.INVOICES.TAX_PENDING') }}
                </span>
              </td>
              <td class="py-4 px-4 text-right">
                <div class="flex items-center justify-end gap-3">
                  <a
                    v-if="invoice.invoiceUrl"
                    :href="invoice.invoiceUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <span>{{ $t('BILLING.INVOICES.PAY') }}</span>
                    <span class="i-lucide-external-link text-xs" />
                  </a>
                  <button
                    class="text-slate-400 hover:text-blue-500 transition-colors"
                    :title="$t('BILLING.INVOICES.SEND_EMAIL')"
                    @click="emit('openSendModal', invoice, 'email')"
                  >
                    <span class="i-lucide-mail" />
                  </button>
                  <button
                    class="text-slate-400 hover:text-emerald-500 transition-colors"
                    :title="$t('BILLING.INVOICES.SEND_WHATSAPP')"
                    @click="emit('openSendModal', invoice, 'whatsapp')"
                  >
                    <span class="i-lucide-message-circle" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div
      v-if="unlinkedTaxInvoices.length"
      class="p-5 border-t border-slate-200 dark:border-slate-800"
    >
      <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
        {{ $t('BILLING.INVOICES.TAX_ORPHAN_TITLE') }}
      </h3>
      <p class="text-sm text-slate-500 mt-1">
        {{ $t('BILLING.INVOICES.TAX_ORPHAN_DESCRIPTION') }}
      </p>
      <div class="overflow-x-auto mt-4">
        <table
          class="min-w-[620px] w-full text-left text-sm text-slate-600 dark:text-slate-300"
        >
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-800/50">
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.TAX_DATE') }}
              </th>
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.TAX_NUMBER') }}
              </th>
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.VALUE') }}
              </th>
              <th class="py-3 px-4 font-medium">
                {{ $t('BILLING.INVOICES.STATUS') }}
              </th>
              <th class="py-3 px-4 font-medium text-right">
                {{ $t('BILLING.INVOICES.TAX_DOCUMENTS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="taxInvoice in unlinkedTaxInvoices"
              :key="taxInvoice.id"
              class="border-b border-slate-100 dark:border-slate-800 last:border-0"
            >
              <td class="py-4 px-4">
                {{
                  taxInvoice.effectiveDate || taxInvoice.scheduledDate || '—'
                }}
              </td>
              <td class="py-4 px-4 font-medium">
                {{ taxInvoice.number || taxInvoice.rpsNumber || '—' }}
              </td>
              <td class="py-4 px-4">
                {{ $t('BILLING.INVOICES.CURRENCY') }}
                {{
                  Number(taxInvoice.value || 0)
                    .toFixed(2)
                    .replace('.', ',')
                }}
              </td>
              <td class="py-4 px-4">
                <span
                  :class="{
                    'bg-emerald-100 text-emerald-700':
                      taxInvoice.status === 'AUTHORIZED',
                    'bg-amber-100 text-amber-700':
                      taxInvoice.status === 'SCHEDULED' ||
                      taxInvoice.status === 'PROCESSING',
                    'bg-rose-100 text-rose-700':
                      taxInvoice.status === 'ERROR' ||
                      taxInvoice.status === 'CANCELED' ||
                      taxInvoice.status === 'CANCELLATION_DENIED',
                  }"
                  class="px-2.5 py-1 rounded-full text-xs font-semibold"
                >
                  {{ taxInvoiceStatus(taxInvoice.status) }}
                </span>
                <p
                  v-if="
                    taxInvoice.status === 'ERROR' && taxInvoice.errorDescription
                  "
                  class="text-xs text-rose-600 mt-1 max-w-xs"
                >
                  {{ taxInvoice.errorDescription }}
                </p>
              </td>
              <td class="py-4 px-4 text-right">
                <div class="flex items-center justify-end gap-3">
                  <a
                    v-if="taxInvoice.pdfUrl"
                    :href="taxInvoice.pdfUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {{ $t('BILLING.INVOICES.TAX_PDF') }}
                  </a>
                  <a
                    v-if="taxInvoice.xmlUrl"
                    :href="taxInvoice.xmlUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-slate-600 hover:text-slate-900 font-medium"
                  >
                    {{ $t('BILLING.INVOICES.TAX_XML') }}
                  </a>
                  <span
                    v-if="!taxInvoice.pdfUrl && !taxInvoice.xmlUrl"
                    class="text-slate-400"
                  >
                    {{ $t('BILLING.INVOICES.TAX_UNAVAILABLE') }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
